const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL || 'https://api.bidoryx.com'}/api`;

// Global token getter function - will be set by App.js
let globalGetToken = null;

// Global trial expired callback - will be set by App.js
let globalOnTrialExpired = null;

/**
 * Set the auth token getter function (called from App.js)
 * @param {Function} getTokenFn - Clerk's getToken function
 */
export function setAuthTokenGetter(getTokenFn) {
  globalGetToken = getTokenFn;
}

/**
 * Set the trial expired callback function (called from App.js)
 * @param {Function} callback - Function to call when trial has expired
 */
export function setTrialExpiredCallback(callback) {
  globalOnTrialExpired = callback;
}

/**
 * Enhanced fetch with automatic auth + accountId headers
 * Reads selected account from localStorage
 */
async function apiFetch(endpoint, options = {}) {
  // Get auth token from Clerk
  let token = null;
  if (globalGetToken) {
    try {
      token = await globalGetToken();
    } catch (error) {
      console.error('Failed to get auth token:', error);
    }
  }
  
  // Get selected account from localStorage
  const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
  const selectedAccount = savedAccount ? JSON.parse(savedAccount) : null;
  
  // Build headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (selectedAccount?.id) {
    headers['X-Account-Id'] = selectedAccount.id;
  }
  
  // Add accountId to URL if not already present and it's a GET request
  let url = `${API_BASE_URL}${endpoint}`;
  if (selectedAccount?.id && !endpoint.includes('accountId=') && (!options.method || options.method === 'GET')) {
    const separator = endpoint.includes('?') ? '&' : '?';
    url += `${separator}accountId=${selectedAccount.id}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });

  // Check for trial expired error (429 with TRIAL_EXPIRED code)
  if (response.status === 429) {
    try {
      const clonedResponse = response.clone();
      const data = await clonedResponse.json();
      if (data.code === 'TRIAL_EXPIRED' && globalOnTrialExpired) {
        globalOnTrialExpired();
      }
    } catch (e) {
      // Ignore JSON parse errors
    }
  }

  return response;
}

export const api = {
  // Health & Connection
  healthCheck: async () => {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.json();
  },

  testConnection: async () => {
    const response = await fetch(`${API_BASE_URL}/test-connection`);
    if (!response.ok) throw new Error('Connection failed');
    return response.json();
  },

  // Campaigns
  getCampaigns: async () => {
    const response = await apiFetch('/campaigns');
    if (!response.ok) throw new Error('Failed to fetch campaigns');
    const data = await response.json();
    return data.data || [];
  },

  getCampaign: async (id) => {
    const response = await apiFetch(`/campaigns/${id}`);
    if (!response.ok) throw new Error('Failed to fetch campaign');
    const data = await response.json();
    return data.campaign;
  },

  createCampaign: async (campaignData) => {
    const response = await apiFetch('/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
    if (!response.ok) throw new Error('Failed to create campaign');
    return response.json();
  },

  updateCampaignStatus: async (id, status) => {
    const response = await apiFetch(`/campaigns/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!response.ok) throw new Error('Failed to update status');
    return response.json();
  },

  updateCampaignBudget: async (id, budget) => {
    const response = await apiFetch(`/campaigns/${id}/budget`, {
      method: 'PATCH',
      body: JSON.stringify({ budget }),
    });
    if (!response.ok) throw new Error('Failed to update budget');
    return response.json();
  },

  // Bulk Operations
  bulkUpdateStatus: async (campaignIds, status) => {
    const response = await apiFetch('/campaigns/bulk/status', {
      method: 'POST',
      body: JSON.stringify({ campaignIds, status }),
    });
    if (!response.ok) throw new Error('Failed to bulk update status');
    return response.json();
  },

  bulkUpdateBudget: async (campaignIds, budget) => {
    const response = await apiFetch('/campaigns/bulk/budget', {
      method: 'POST',
      body: JSON.stringify({ campaignIds, budget }),
    });
    if (!response.ok) throw new Error('Failed to bulk update budget');
    return response.json();
  },

  // Ad Groups
  getAdGroups: async (campaignId) => {
    const response = await apiFetch(`/campaigns/${campaignId}/adgroups`);
    if (!response.ok) throw new Error('Failed to fetch ad groups');
    const data = await response.json();
    return data.data || [];
  },

  createAdGroup: async (campaignId, adGroupData) => {
    const response = await apiFetch(`/campaigns/${campaignId}/adgroups`, {
      method: 'POST',
      body: JSON.stringify(adGroupData),
    });
    if (!response.ok) throw new Error('Failed to create ad group');
    return response.json();
  },

  // Keywords
  getKeywords: async (adGroupId) => {
    const response = await apiFetch(`/adgroups/${adGroupId}/keywords`);
    if (!response.ok) throw new Error('Failed to fetch keywords');
    const data = await response.json();
    return data.data || [];
  },

  addKeyword: async (adGroupId, keywordData) => {
    const response = await apiFetch(`/adgroups/${adGroupId}/keywords`, {
      method: 'POST',
      body: JSON.stringify(keywordData),
    });
    if (!response.ok) throw new Error('Failed to add keyword');
    return response.json();
  },

  // Negative Keywords
  getNegativeKeywords: async (campaignId) => {
    const response = await apiFetch(`/campaigns/${campaignId}/negative-keywords`);
    if (!response.ok) throw new Error('Failed to fetch negative keywords');
    const data = await response.json();
    return data.data || [];
  },

  addNegativeKeyword: async (campaignId, keywordData) => {
    const response = await apiFetch(`/campaigns/${campaignId}/negative-keywords`, {
      method: 'POST',
      body: JSON.stringify(keywordData),
    });
    if (!response.ok) throw new Error('Failed to add negative keyword');
    return response.json();
  },

  removeNegativeKeyword: async (campaignId, criterionId) => {
    const response = await apiFetch(`/campaigns/${campaignId}/negative-keywords/${criterionId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to remove negative keyword');
    return response.json();
  },

  // Responsive Search Ads
  getAds: async (adGroupId) => {
    const response = await apiFetch(`/adgroups/${adGroupId}/ads`);
    if (!response.ok) throw new Error('Failed to fetch ads');
    const data = await response.json();
    return data.data || [];
  },

  createAd: async (adGroupId, adData) => {
    const response = await apiFetch(`/adgroups/${adGroupId}/ads`, {
      method: 'POST',
      body: JSON.stringify(adData),
    });
    if (!response.ok) throw new Error('Failed to create ad');
    return response.json();
  },

  // Export
  exportCampaigns: async () => {
    const response = await apiFetch('/export/campaigns');
    if (!response.ok) throw new Error('Failed to export campaigns');
    const data = await response.json();
    return data.data;
  },

  exportKeywords: async (adGroupId) => {
    const response = await apiFetch(`/export/keywords/${adGroupId}`);
    if (!response.ok) throw new Error('Failed to export keywords');
    const data = await response.json();
    return data.data;
  },

  // Analytics
  getTrends: async (days = 7) => {
    const response = await apiFetch(`/performance/trends?days=${days}`);
    if (!response.ok) throw new Error('Failed to fetch trends');
    const data = await response.json();
    return data.data || [];
  },

  getDashboardStats: async () => {
    const response = await apiFetch('/dashboard/stats');
    if (!response.ok) throw new Error('Failed to fetch stats');
    const data = await response.json();
    return data.stats;
  },
};
